import { MedplumClient } from "@medplum/core";

// Hard fuse on auth failure. Medplum's `onUnauthenticated` only fires after
// its internal refresh-and-retry dance, and not on every 401 path — leaving
// room for callers (hooks, route guards, cached profile reads) to keep
// hammering `/auth/me` while the storm spirals. We wrap the global fetch
// instead: the first 401 to an authed endpoint clears Medplum state and
// bounces to `/login`. Guarded so we fire the side effect exactly once.
let authFailureHandled = false;
function isAuthedEndpoint(url: string): boolean {
  if (url.includes("/oauth2/token")) return false;
  if (url.includes("/auth/login")) return false;
  if (url.includes("/auth/register")) return false;
  return url.includes("/auth/me") || url.includes("/fhir/");
}
// Typed as a plain call signature rather than `typeof fetch`: under
// `@types/bun`, `typeof fetch` carries statics (`preconnect`) a plain wrapper
// can't have, and it's only ever called, so the call signature is all that
// matters here.
const authAwareFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const response = await fetch(input, init);
  if (response.status === 401 && !authFailureHandled) {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    if (isAuthedEndpoint(url) && window.location.pathname !== "/login") {
      authFailureHandled = true;
      medplum.clear();
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
  }
  return response;
};

export const medplum = new MedplumClient({
  baseUrl: import.meta.env.VITE_MEDPLUM_BASE_URL || "http://localhost:8103",
  clientId: import.meta.env.VITE_MEDPLUM_CLIENT_ID,
  storagePrefix: "@medplum:ai:",
  fetch: authAwareFetch as typeof fetch,
  onUnauthenticated: () => {
    // Backstop for paths that the fetch wrapper missed (e.g. Medplum-internal
    // calls that don't go through the global fetch). Clear stale tokens first;
    // otherwise background refresh keeps re-hitting /auth/me with the same dead
    // credential while the redirect unloads. Guarded to fire exactly once.
    if (authFailureHandled) return;
    authFailureHandled = true;
    medplum.clear();
    if (window.location.pathname !== "/login") {
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
  },
});
